import classnames from 'classnames';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';

import Icon from 'components/Icon/Icon';
import { useConfig } from 'config';
import { SwitcherState, useUISettings } from 'store';
import { Translation } from 'utils/translations';

import styles from './Switcher.module.scss';

type SwitcherProps = {
  wide?: boolean;
};

const switcherItems: Record<
  SwitcherState,
  {
    label: string;
    icon: string;
    color: string;
    link: string;
  }
> = {
  [SwitcherState.casino]: {
    label: 'casino',
    icon: 'casino',
    color: 'var(--color-casino)',
    link: '/casino',
  },
  [SwitcherState.sport]: {
    label: 'sport',
    icon: 'sport',
    color: 'var(--color-sport)',
    link: '/sportsbook',
  },
};

const Switcher: FC<SwitcherProps> = ({ wide = false }) => {
  const [{ switcher }, { setSwitcherState }] = useUISettings();
  const [, navigate] = useLocation();
  const isCasinoEnabled = useConfig('config.siteConfig.casino.enabled');
  const isSportsbookEnabled = useConfig('config.siteConfig.sportsbook.enabled');

  const availableValues: SwitcherState[] = useMemo(
    () =>
      [
        isCasinoEnabled && SwitcherState.casino,
        isSportsbookEnabled && SwitcherState.sport,
      ].filter((value) => !!value),
    [isCasinoEnabled, isSportsbookEnabled],
  );

  const toggleSwitcher = useCallback(
    (redirect = true) => {
      const desirableValue =
        switcher === SwitcherState.casino
          ? SwitcherState.sport
          : SwitcherState.casino;
      const newValue = availableValues.includes(desirableValue)
        ? desirableValue
        : switcher;

      setSwitcherState(newValue);
      if (redirect) {
        navigate(switcherItems[newValue].link);
      }
    },
    [switcher, setSwitcherState, navigate, availableValues],
  );

  useEffect(() => {
    if (!availableValues.includes(switcher)) {
      toggleSwitcher(false);
    }
  }, [switcher, toggleSwitcher, availableValues]);

  if (availableValues.length === 0) {
    return;
  }

  return (
    <button
      type="button"
      className={classnames(styles.wrapper, {
        [styles.wide]: wide,
      })}
      onClick={() => toggleSwitcher()}
    >
      <div
        className={classnames(styles.icon, {
          [styles.active]: switcher === SwitcherState.casino,
        })}
        style={
          switcher === SwitcherState.casino
            ? { background: switcherItems[SwitcherState.casino].color }
            : undefined
        }
      >
        <Icon name={switcherItems[SwitcherState.casino].icon} />
      </div>
      {wide && (
        <div className={styles.label}>
          <Translation message={switcherItems[switcher].label} />
        </div>
      )}
      <div
        className={classnames(styles.icon, {
          [styles.active]: switcher === SwitcherState.sport,
        })}
        style={
          switcher === SwitcherState.sport
            ? { background: switcherItems[SwitcherState.sport].color }
            : undefined
        }
      >
        <Icon name={switcherItems[SwitcherState.sport].icon} />
      </div>
    </button>
  );
};

export default Switcher;
